import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Heart,
  AlertCircle,
  X,
  CheckCircle,
  Calendar,
} from 'lucide-react'
import api, { getAllCattle } from '../../services/api'
import PageHeader from '../../components/PageHeader'
import clsx from 'clsx'

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

const getReproductiveEvents = (ranchId, params = {}) =>
  api.get('/dairy/reproductive-events', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

const createReproductiveEvent = (data) =>
  api.post('/dairy/reproductive-events', data).then(res => res.data)

const getDairyCattle = (ranchId) =>
  getAllCattle(ranchId, { status: 'active', category: 'cow' })

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const eventTypes = [
  { value: 'heat_detected', label: 'Heat Detected' },
  { value: 'artificial_insemination', label: 'Artificial Insemination' },
  { value: 'natural_service', label: 'Natural Service' },
  { value: 'pregnancy_check_positive', label: 'Pregnancy Check - Positive' },
  { value: 'pregnancy_check_negative', label: 'Pregnancy Check - Negative' },
  { value: 'pregnancy_check_open', label: 'Pregnancy Check - Open' },
  { value: 'dry_off', label: 'Dry Off' },
  { value: 'abortion', label: 'Abortion' },
  { value: 'retained_placenta', label: 'Retained Placenta' },
  { value: 'metritis', label: 'Metritis' },
]

const eventTypeLabels = Object.fromEntries(eventTypes.map((t) => [t.value, t.label]))

const eventTypeColors = {
  heat_detected: 'bg-pink-100 text-pink-800',
  artificial_insemination: 'bg-blue-100 text-blue-800',
  natural_service: 'bg-indigo-100 text-indigo-800',
  pregnancy_check_positive: 'bg-green-100 text-green-800',
  pregnancy_check_negative: 'bg-red-100 text-red-800',
  pregnancy_check_open: 'bg-amber-100 text-amber-800',
  dry_off: 'bg-gray-100 text-gray-800',
  abortion: 'bg-red-100 text-red-800',
  retained_placenta: 'bg-orange-100 text-orange-800',
  metritis: 'bg-purple-100 text-purple-800',
}

// ---------------------------------------------------------------------------
// Log Event Modal
// ---------------------------------------------------------------------------

function LogEventModal({ ranchId, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    ranch_id: ranchId,
    animal_id: '',
    event_type: 'heat_detected',
    event_date: new Date().toISOString().split('T')[0],
    sire_breed: '',
    ai_technician: '',
    insemination_number: '',
    expected_calving_date: '',
    days_pregnant: '',
    notes: '',
  })
  const [error, setError] = useState('')

  const { data: cattleData } = useQuery({
    queryKey: ['dairyCattle', ranchId],
    queryFn: () => getDairyCattle(ranchId),
  })

  const mutation = useMutation({
    mutationFn: createReproductiveEvent,
    onSuccess: () => onSuccess(),
    onError: (err) => setError(err.response?.data?.detail || 'Failed to log reproductive event'),
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')
    if (!formData.animal_id) {
      setError('Please select a cow')
      return
    }

    const payload = {
      ranch_id: formData.ranch_id,
      animal_id: formData.animal_id,
      event_type: formData.event_type,
      event_date: formData.event_date,
      notes: formData.notes || null,
    }

    // Conditional fields
    const needsSire = ['artificial_insemination', 'natural_service'].includes(formData.event_type)
    if (needsSire) {
      if (formData.sire_breed) payload.sire_breed = formData.sire_breed
      if (formData.ai_technician) payload.ai_technician = formData.ai_technician
      if (formData.insemination_number) payload.insemination_number = parseInt(formData.insemination_number)
    }

    if (formData.event_type === 'pregnancy_check_positive') {
      if (formData.expected_calving_date) payload.expected_calving_date = formData.expected_calving_date
      if (formData.days_pregnant) payload.days_pregnant = parseInt(formData.days_pregnant)
    }

    mutation.mutate(payload)
  }

  const cows = cattleData || []
  const showSireFields = ['artificial_insemination', 'natural_service'].includes(formData.event_type)
  const showPregnancyFields = formData.event_type === 'pregnancy_check_positive'

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Log Reproductive Event</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Animal */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cow *</label>
            <select
              required
              value={formData.animal_id}
              onChange={(e) => setFormData({ ...formData, animal_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="">Select cow...</option>
              {cows.map((cow) => (
                <option key={cow.id} value={cow.id}>
                  {cow.local_name || cow.rfid_tag} ({cow.tag_id || cow.rfid_tag})
                </option>
              ))}
            </select>
          </div>

          {/* Event type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Type *</label>
            <select
              value={formData.event_type}
              onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              {eventTypes.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
            <input
              type="date"
              required
              value={formData.event_date}
              onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          {/* Sire fields */}
          {showSireFields && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sire Breed</label>
                <input
                  type="text"
                  value={formData.sire_breed}
                  onChange={(e) => setFormData({ ...formData, sire_breed: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="e.g. Holstein, Jersey"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">AI Technician</label>
                  <input
                    type="text"
                    value={formData.ai_technician}
                    onChange={(e) => setFormData({ ...formData, ai_technician: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Insemination #</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.insemination_number}
                    onChange={(e) => setFormData({ ...formData, insemination_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
              </div>
            </>
          )}

          {/* Pregnancy fields */}
          {showPregnancyFields && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expected Calving</label>
                <input
                  type="date"
                  value={formData.expected_calving_date}
                  onChange={(e) => setFormData({ ...formData, expected_calving_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Days Pregnant</label>
                <input
                  type="number"
                  min="0"
                  max="290"
                  value={formData.days_pregnant}
                  onChange={(e) => setFormData({ ...formData, days_pregnant: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Optional observations..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              <CheckCircle className="h-4 w-4" />
              {mutation.isPending ? 'Saving...' : 'Log Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function ReproductiveCalendar({ ranchId }) {
  const queryClient = useQueryClient()
  const [showLogModal, setShowLogModal] = useState(false)
  const [page, setPage] = useState(1)
  const limit = 20

  const { data, isLoading, error } = useQuery({
    queryKey: ['reproductiveEvents', ranchId, page],
    queryFn: () => getReproductiveEvents(ranchId, { limit, offset: (page - 1) * limit }),
  })

  const events = data?.items || data || []
  const totalCount = data?.total || events.length
  const totalPages = Math.ceil(totalCount / limit)

  // Pipeline stats
  const stats = data?.pipeline_stats || {}
  const heatsDetected = stats.heats_detected ?? events.filter((e) => e.event_type === 'heat_detected').length
  const aiServices = stats.ai_services ?? events.filter((e) => e.event_type === 'artificial_insemination').length
  const pregPositive = stats.pregnancy_positive ?? events.filter((e) => e.event_type === 'pregnancy_check_positive').length
  const notBred90 = stats.not_bred_90dim ?? 0

  const handleSuccess = () => {
    setShowLogModal(false)
    queryClient.invalidateQueries({ queryKey: ['reproductiveEvents', ranchId] })
  }

  if (error) {
    return (
      <div className="card p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900">Failed to load reproductive events</h3>
        <p className="text-gray-500 mt-1">{error.message}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reproductive Calendar"
        subtitle="Heat detection, AI services, and pregnancy tracking"
        icon={Heart}
        actions={
          <button
            onClick={() => setShowLogModal(true)}
            className="btn bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Log Event
          </button>
        }
      />

      {/* Pipeline Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card p-5">
          <div className="text-2xl font-bold text-pink-600">{heatsDetected}</div>
          <div className="text-sm text-gray-500 mt-1">Heats Detected</div>
        </div>
        <div className="card p-5">
          <div className="text-2xl font-bold text-blue-600">{aiServices}</div>
          <div className="text-sm text-gray-500 mt-1">AI Services</div>
        </div>
        <div className="card p-5">
          <div className="text-2xl font-bold text-green-600">{pregPositive}</div>
          <div className="text-sm text-gray-500 mt-1">Pregnancy Positive</div>
        </div>
        <div className="card p-5">
          <div className="text-2xl font-bold text-amber-600">{notBred90}</div>
          <div className="text-sm text-gray-500 mt-1">Not Bred &gt;90 DIM</div>
        </div>
      </div>

      {/* Events Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12">
            <Heart className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No reproductive events logged</h3>
            <p className="text-gray-500 mt-1">Log heats, AI services, and pregnancy checks here</p>
          </div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cow</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sire Breed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Result / Notes</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {events.map((evt) => (
                  <tr key={evt.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {new Date(evt.event_date).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {evt.animal_name || evt.cow_name || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={clsx(
                        'px-2 py-1 text-xs font-medium rounded-full',
                        eventTypeColors[evt.event_type] || 'bg-gray-100 text-gray-800'
                      )}>
                        {eventTypeLabels[evt.event_type] || evt.event_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {evt.sire_breed || '--'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {evt.notes || '--'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <div className="text-sm text-gray-500">
                  Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalCount)} of {totalCount}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {showLogModal && (
        <LogEventModal
          ranchId={ranchId}
          onClose={() => setShowLogModal(false)}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  )
}
